import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { OptimizedImage, TrustpilotWidget } from '../common';
import dynamic from 'next/dynamic';

import styles from '../../styles/components/sections/HeroSectionWithForm.module.css';
import { useDarkMode } from '../../utils/hooks/useDarkMode';
import { isSafari } from '../../utils/helpers/utils';

// Lazy load BookingForm with dynamic import for optimal performance
const BookingForm = dynamic(() => import('../forms/BookingForm'), {
    loading: () => <SkeletonForm />,
    ssr: false // Don't render on server to improve initial load
});

// Loading skeleton for the form
const SkeletonForm = ({ isDarkMode = false }) => {
    return (
        <div className={`${styles.skeletonForm} ${isDarkMode ? styles.skeletonFormDark : ''}`}>
            <div className={`${styles.skeletonHeader} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
            <div className={styles.skeletonRow}>
                <div className={`${styles.skeletonInput} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
                <div className={`${styles.skeletonInput} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
            </div>
            <div className={styles.skeletonRow}>
                <div className={`${styles.skeletonInput} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
                <div className={`${styles.skeletonInput} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
            </div>
            <div className={styles.skeletonRow}>
                <div className={`${styles.skeletonInput} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
                <div className={`${styles.skeletonInput} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
            </div>
            <div className={`${styles.skeletonButton} ${isDarkMode ? styles.skeletonDark : ''}`}></div>
        </div>
    );
};

const HeroSectionWithForm = ({ data, phoneNumber }) => {
    const { isDarkMode, isThemeInitialized } = useDarkMode();
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [shouldLoadForm, setShouldLoadForm] = useState(false);
    const sectionRef = useRef(null);

    // Check if booking form should be shown
    const isBookingFormShown = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.get_quote || data?.call_button) {
            return true;
        }
        // Fallback to original structure
        return data?.isBookingFormShown ?? true; // Default to true for this component
    }, [data?.get_quote, data?.call_button, data?.isBookingFormShown]);

    // Memoize expensive computations with enhanced data structure support
    const backgroundImage = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.Image?.image?.url) {
            return data.Image.image.url;
        }
        // Fallback to original structure
        return data?.Image?.image?.url || null;
    }, [data?.Image?.image?.url]);
    
    const mobileBackgroundImage = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.mobileImage?.image?.url) {
            return data.mobileImage.image.url;
        }
        // Fallback to original structure
        return data?.MobileImage?.image?.url || null;
    }, [data?.mobileImage?.image?.url, data?.MobileImage?.image?.url]);
    
    // Enhanced phone number handling
    const displayPhoneNumber = useMemo(() => {
        // Check for EnhancedHeroSection structure first
        if (data?.phone) {
            return { value: data.phone };
        }
        // Fallback to original structure
        return phoneNumber;
    }, [data?.phone, phoneNumber]);

    // Section class - Use CSS classes for responsive behavior to prevent CLS
    // Apply different height classes based on whether form is shown
    const sectionClass = useMemo(() => {
        const classes = [
            'position-relative',
            'd-flex',
            'align-items-center',
            'text-left',
            styles.heroSection
        ];
        
        // Add conditional height class
        if (isBookingFormShown) {
            classes.push(styles.withBookingForm);
        } else {
            classes.push(styles.minimalContent);
        }
        
        return classes.join(' ');
    }, [isBookingFormShown]);
    
    // Container class - Use CSS classes for responsive behavior to prevent CLS
    const containerClass = useMemo(() => {
        return [
            'tw-container',
            'h-100',
            'd-flex',
            'align-items-center',
            'justify-content-center',
            'mt-2',
            'gap-4',
            styles.heroContainer
        ].join(' ');
    }, []);
    
    // Row class - Use CSS for responsive behavior to prevent CLS
    const rowClass = useMemo(() => {
        return [
            'row', 
            'w-100', 
            'align-items-center',
            'justify-content-center',
            styles.heroRow
        ].join(' ');
    }, []);
    
    // Content container class - Left side content
    const contentContainerClass = useMemo(() => {
        const classes = ['col-12', styles.contentContainer];
        
        // If booking form is shown, split into two columns on desktop
        if (isBookingFormShown) {
            classes.push('col-lg-6');
        }
        
        return classes.join(' ');
    }, [isBookingFormShown]);
    
    // Form container class - Right side form
    const formContainerClass = useMemo(() => {
        return [
            'col-12',
            'col-lg-6',
            styles.formContainer
        ].join(' ');
    }, []);

    // Optimized image loading - prevent reflows
    useEffect(() => {
        if (isThemeInitialized && backgroundImage) {
            // Use requestAnimationFrame to prevent forced reflows
            const loadImage = () => {
                // Image will load automatically via OptimizedImage component
                // No DOM queries or style changes here
            };
            
            requestAnimationFrame(loadImage);
        }
    }, [isThemeInitialized, backgroundImage]);

    // Delay form loading slightly to prioritize hero content
    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldLoadForm(true);
        }, 100); // Minimal delay to ensure hero content renders first
        
        return () => clearTimeout(timer);
    }, []);

    const handleImageLoad = useCallback(() => {
        // Use requestAnimationFrame to prevent forced reflows
        requestAnimationFrame(() => {
            setIsImageLoaded(true);
        });
    }, []);

    const placeholderBackgroundColor = useMemo(() => (isDarkMode ? '#1a1a1a' : '#f8f9fa'), [isDarkMode]);
    const overlayBackgroundColor = useMemo(() => (isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'), [isDarkMode]);

    return (
        <section ref={sectionRef} className={sectionClass}>
            {/* Background Image */}
            <div className="position-absolute top-0 start-0 w-100 h-100 overflow-hidden">
                {/* Loading placeholder */}
                {backgroundImage && !isImageLoaded && (
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{
                            backgroundColor: placeholderBackgroundColor,
                        }}
                    />
                )}

                {/* Dark overlay */}
                <div
                    className={`position-absolute top-0 start-0 w-100 h-100 ${styles.imageOverlay} ${isDarkMode ? styles.dark : ''}`}
                    style={{
                        backgroundColor: overlayBackgroundColor,
                    }}
                />
            </div>

            <div className={containerClass}>
                <div className={rowClass}>
                    {/* Left Side - Content */}
                    <div className={contentContainerClass}>
                        {/* Heading */}
                        <p
                            className={styles.heading}
                            style={{ 
                                fontFamily: "'Gilroy-Bold', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontDisplay: 'swap',
                                textRendering: 'optimizeSpeed'
                            }}
                        >
                            {data?.title}
                        </p>

                        {/* Subtitle */}
                        {(data?.subtitle || data?.subTitle) && (
                            <p 
                                className={styles.subtitle}
                                style={{ 
                                    fontFamily: "'Gilroy-Regular', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                    fontDisplay: 'swap',
                                    textRendering: 'optimizeSpeed'
                                }}
                            >
                                {data?.subtitle ?? data?.subTitle}
                            </p>
                        )}
                    </div>

                    {/* Right Side - Booking Form (only if isBookingFormShown is true) */}
                    {isBookingFormShown && (
                        <div className={formContainerClass}>
                            <div className={styles.formWrapper}>
                                {shouldLoadForm ? (
                                    <Suspense fallback={<SkeletonForm isDarkMode={isDarkMode} />}>
                                        <BookingForm />
                                    </Suspense>
                                ) : (
                                    <SkeletonForm isDarkMode={isDarkMode} />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default HeroSectionWithForm;

